import React, { useEffect, useState } from "react";
import { Autocomplete, Card, Grid, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Spinner from "components/Spinner/Spinner";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import {
  deleteS1TestSubmission,
  getEvent,
  getS1SiteNames,
  getS1TestSubmissions,
} from "helpers/events";
import { showErrorToast, showSuccessToast } from "helpers/helpers";
import JsonData from "layouts/pages/details/settings/components/JsonData";

function RemoveTestS1() {
  const [journals, setJournals] = useState([]);
  const [selectedJournal, setSelectedJournal] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [step, setStep] = useState(1);
  const [eventData, setEventData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchJournals = async () => {
      try {
        const response = await getS1SiteNames();
        setJournals(response.data.journals ?? []);
      } catch {
        showErrorToast("Failed to load journals");
      }
    };
    fetchJournals();
  }, []);

  const handleJournalChange = async (_event, value) => {
    setSelectedJournal(value);
    setSelectedSubmission(null);
    setSubmissions([]);
    if (!value) return;
    try {
      setIsLoading(true);
      const response = await getS1TestSubmissions(value.siteName);
      setSubmissions(response.data.submissions ?? []);
    } catch {
      showErrorToast("Failed to load test submissions for this journal");
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewSubmission = async () => {
    if (!selectedSubmission) return;
    try {
      setIsLoading(true);
      const response = await getEvent(selectedSubmission.uuid);
      setEventData(response.data);
      setStep(2);
    } catch {
      showErrorToast("Failed to load event data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedSubmission) return;
    try {
      setIsLoading(true);
      await deleteS1TestSubmission(selectedSubmission.uuid);
      showSuccessToast();
      setStep(1);
      setSelectedSubmission(null);
      setEventData(null);
      // Refresh submissions list for the current journal
      if (selectedJournal) {
        const response = await getS1TestSubmissions(selectedJournal.siteName);
        setSubmissions(response.data.submissions ?? []);
      }
    } catch {
      showErrorToast("Failed to delete test submission");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setEventData(null);
  };

  const getSubmissionLabel = (option) => {
    if (!option) return "";
    const title = option.submission_title ? ` — ${option.submission_title}` : "";
    return `${option.submission_id}${title}`;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={3}>
        <Grid container spacing={3} justifyContent="center">
          {/* Step 1 — Select journal and submission */}
          {step === 1 && (
            <Grid item xs={12} md={6}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" gutterBottom>
                    Remove test data S1
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mb={2}>
                    Select a journal to see its test submissions, then choose a submission to review
                    and delete.
                  </MDTypography>

                   <MDBox mb={2}>
                     <MDTypography variant="caption" fontWeight="medium">
                       Journal
                     </MDTypography>
                     <Autocomplete
                       options={journals}
                       value={selectedJournal}
                       onChange={handleJournalChange}
                       getOptionLabel={(option) => option.journalTitle ?? ""}
                       isOptionEqualToValue={(option, value) => option.siteName === value.siteName}
                       renderInput={(params) => (
                         <TextField {...params} placeholder="Select journal" />
                       )}
                     />
                   </MDBox>

                  <MDBox mb={3}>
                     <MDTypography variant="caption" fontWeight="medium">
                       Test submission
                     </MDTypography>
                     <Autocomplete
                       options={submissions}
                       value={selectedSubmission}
                       onChange={(_e, value) => setSelectedSubmission(value)}
                       getOptionLabel={getSubmissionLabel}
                       isOptionEqualToValue={(option, value) => option.uuid === value.uuid}
                       disabled={!selectedJournal || isLoading}
                       noOptionsText={
                        selectedJournal
                          ? "No test submissions found for this journal"
                          : "Select a journal first"
                      }
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select test submission" />
                      )}
                    />
                  </MDBox>

                  {isLoading && <Spinner />}

                  <MDBox display="flex" justifyContent="flex-end">
                    <MDButton
                      variant="gradient"
                      color="info"
                      disabled={!selectedSubmission || isLoading}
                      onClick={handleViewSubmission}
                    >
                      View submission
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Step 2 — Review and confirm deletion */}
          {step === 2 && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3}>
                  <MDTypography variant="h6" gutterBottom>
                    Review event data
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mb={1}>
                    Submission:{" "}
                    <strong>
                      {selectedSubmission?.submission_id}
                      {selectedSubmission?.submission_title
                        ? ` — ${selectedSubmission.submission_title}`
                        : ""}
                    </strong>
                  </MDTypography>
                  <MDTypography variant="body2" color="error" mb={2}>
                    This will permanently delete all bronze and silver data for this submission,
                    including all authors, affiliations, funders and reviewer records. This action
                    cannot be undone.
                  </MDTypography>

                  {isLoading && <Spinner />}

                  {!isLoading && eventData && (
                    <MDBox mb={3}>
                      {eventData.silver && (
                        <MDBox mb={2}>
                          <MDTypography variant="caption" fontWeight="medium">
                            Silver event
                          </MDTypography>
                          <JsonData data={eventData.silver} />
                        </MDBox>
                      )}
                      {eventData.bronze && (
                        <MDBox>
                          <MDTypography variant="caption" fontWeight="medium">
                            Bronze event
                          </MDTypography>
                          <JsonData data={eventData.bronze} />
                        </MDBox>
                      )}
                    </MDBox>
                  )}

                  <MDBox display="flex" gap={2}>
                    <MDButton
                      variant="gradient"
                      color="secondary"
                      onClick={handleBack}
                      disabled={isLoading}
                    >
                      Back
                    </MDButton>
                    <MDButton
                      variant="gradient"
                      color="error"
                      onClick={handleConfirmDelete}
                      disabled={isLoading}
                    >
                      Confirm delete
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          )}
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default RemoveTestS1;
